// src/components/NewFormulation.jsx
import React, { useState, useMemo } from "react";
import "./NewFormulation.css";
import { PRODUCTS, BASES, SIZES_L, COLOR_CHARTS } from "../data/autofills";
import { auth } from "../data/auth";

const emptyRow = () => ({
  id: typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Math.random()}${Date.now()}`,
  colorant: "",
  value: ""
});

const newBlank = () => ({
  name: "",
  code: "",
  product: "",
  base: "",
  chart: "",
  sizeL: 10,
  confidence: "Normal",
  baseType: "Base10 (New)",
  notes: "",
  rows: [emptyRow(), emptyRow(), emptyRow(), emptyRow()],
  createdAt: Date.now(),
  updatedAt: Date.now()
});

export default function NewFormulation({ onSave, onError }) {
  const [draft, setDraft] = useState(newBlank());

  const totalColorants = useMemo(
    () => draft.rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0),
    [draft.rows]
  );

  function setField(key, value) {
    setDraft(d => ({ ...d, [key]: value, updatedAt: Date.now() }));
  }

  function setRow(idx, patch) {
    setDraft(d => {
      const rows = [...d.rows];
      rows[idx] = { ...rows[idx], ...patch };
      return { ...d, rows, updatedAt: Date.now() };
    });
  }

  function addRow() {
    setDraft(d => ({ ...d, rows: [...d.rows, emptyRow()], updatedAt: Date.now() }));
  }

  function removeRow(idx) {
    setDraft(d => ({
      ...d,
      rows: d.rows.filter((_, i) => i !== idx),
      updatedAt: Date.now()
    }));
  }

  function resetDraft() {
    setDraft(newBlank());
  }

  function save(e) {
    e?.preventDefault?.();
    if (!draft.name.trim()) {
      onError?.({
        name: draft.name.trim(),
        msg: "Enter a color name."
      });
      return;
    }
    const payload = {
      name: draft.name.trim(),
      code: draft.code.trim(),
      product: draft.product.trim(),
      base: draft.base.trim(),
      chart: draft.chart.trim(),
      sizeL: Number(draft.sizeL) || 0,
      confidence: draft.confidence,
      baseType: draft.baseType,
      notes: draft.notes.trim(),
      rows: draft.rows.map(r => ({
        colorant: r.colorant.trim(),
        value: String(r.value).trim()
      })),
      creatorEmail: auth.currentUser?.email || "unknown",
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    onSave?.(payload);
    resetDraft();
  }

  return (
    <section className="nf card">
      {/* Header */}
      <div className="nf__header">
        <h2 className="nf__title">New Formulation</h2>
        <p className="nf__subtitle">
          Enter color details, then add colorant rows.
        </p>
      </div>

      {/* A) Color Details */}
      <div className="nf__segment nf__details">
        <h3 className="nf__segmentTitle">Color Details</h3>
        <div className="nf__grid">
          {/* Color Name */}
          <label className="nf__labeled">
            <span className="nf__label">Color Name</span>
            <input
              className="nf__input"
              value={draft.name}
              onChange={e => setField("name", e.target.value)}
              placeholder="e.g: Nimo"
            />
          </label>

          {/* Code */}
          <label className="nf__labeled">
            <span className="nf__label">Code</span>
            <input
              className="nf__input"
              value={draft.code}
              onChange={e => setField("code", e.target.value)}
              placeholder="e.g: PT-274"
            />
          </label>

          {/* Product */}
          <label className="nf__labeled">
            <span className="nf__label">Product</span>
            <div className="nf__combo">
              <input
                className="nf__input nf__input--combo"
                value={draft.product}
                onChange={e => setField("product", e.target.value)}
                placeholder="e.g: Living shield Max"
              />
              <select
                className="nf__select nf__overlay"
                defaultValue=""
                onChange={e => { setField("product", e.target.value); e.target.selectedIndex = 0; }}
              >
                <option value="" />
                {PRODUCTS.map(p => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          {/* Base */}
          <label className="nf__labeled">
            <span className="nf__label">Base</span>
            <div className="nf__combo">
              <input
                className="nf__input nf__input--combo"
                value={draft.base}
                onChange={e => setField("base", e.target.value)}
                placeholder="e.g: N"
              />
              <select
                className="nf__select nf__overlay"
                defaultValue=""
                onChange={e => { setField("base", e.target.value); e.target.selectedIndex = 0; }}
              >
                <option value="" />
                {BASES.map(b => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          {/* Size (L) */}
          <label className="nf__labeled">
            <span className="nf__label">Size (L)</span>
            <div className="nf__combo">
              <input
                type="number"
                step="0.01"
                className="nf__input nf__input--combo"
                value={draft.sizeL}
                onChange={e => setField("sizeL", e.target.value)}
                placeholder="e.g: 10"
              />
              <select
                className="nf__select nf__overlay"
                defaultValue=""
                onChange={e => { setField("sizeL", Number(e.target.value)); e.target.selectedIndex = 0; }}
              >
                <option value="" />
                {SIZES_L.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          {/* Color Chart */}
          <label className="nf__labeled">
            <span className="nf__label">Color Chart</span>
            <div className="nf__combo">
              <input
                className="nf__input nf__input--combo"
                value={draft.chart}
                onChange={e => setField("chart", e.target.value)}
                placeholder="e.g: Resene Whites and Neutrals"
              />
              <select
                className="nf__select nf__overlay"
                defaultValue=""
                onChange={e => { setField("chart", e.target.value); e.target.selectedIndex = 0; }}
              >
                <option value="" />
                {COLOR_CHARTS.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          {/* Confidence */}
          <label className="nf__labeled">
            <div className="nf__hint-wrapper">
              <span className="nf__label">Confidence</span>
              <div className="nf__tooltip">
                How confident are you that this formula is in the correct Base system, or that it is correct overall
              </div>
            </div>
            <select
              className="nf__input"
              value={draft.confidence}
              onChange={e => setField("confidence", e.target.value)}
              required
            >
              <option value="Low">Low</option>
              <option value="Normal">Normal</option>
              <option value="High">High</option>
            </select>
          </label>

          {/* Base Type */}
          <label className="nf__labeled">
            <div className="nf__hint-wrapper">
              <span className="nf__label">Base Type</span>
              <div className="nf__tooltip">
                Base 8 is for the colour wheel carousel and Base 10 is also known as PSL and is for everything else
              </div>
            </div>
            <select
              className="nf__input"
              value={draft.baseType}
              onChange={e => setField("baseType", e.target.value)}
              required
            >
              <option value="Base10 (New)">Base10 (New)</option>
              <option value="Base8 (Old)">Base8 (Old)</option>
            </select>
          </label>

          {/* Notes */}
          <label className="nf__labeled nf__notes">
            <span className="nf__label">Notes</span>
            <input
              className="nf__input"
              value={draft.notes}
              onChange={e => setField("notes", e.target.value)}
              placeholder="Optional notes…"
            />
          </label>
        </div>
      </div>

      {/* B) Colorant Rows */}
      <div className="nf__segment nf__rows nf__details">
        <h3 className="nf__segmentTitle">Colorant Rows</h3>
        <div className="nf__tableWrap">
          <table className="nf__table">
            <thead>
              <tr>
                <th>Colourant</th>
                <th>Value</th>
                <th className="nf__right"></th>
              </tr>
            </thead>
            <tbody>
              {draft.rows.map((row, idx) => (
                <tr key={row.id}>
                  <td>
                    <input
                      className="nf__input"
                      placeholder="e.g., B1"
                      value={row.colorant}
                      onChange={e => setRow(idx, { colorant: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="nf__input"
                      placeholder="e.g., 12.5"
                      value={row.value}
                      onChange={e => setRow(idx, { value: e.target.value })}
                    />
                  </td>
                  <td className="nf__right">
                    <button
                      type="button"
                      className="nf__btn nf__btn--subtle"
                      onClick={() => removeRow(idx)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="nf__addRowCell">
                  <button
                    type="button"
                    className="nf__btn nf__btn--ghost nf__btn--addRowInTable"
                    onClick={addRow}
                  >
                    + Add Row
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* C) Save Button */}
      <div className="nf__actions">
        <button
          type="button"
          className="nf__btn nf__btn--ghost nf__btn--save"
          onClick={save}
        >
          Save
        </button>
      </div>
    </section>
  );
}
